import { Box, Button, Stack, Typography } from '@mui/material'
import { useCallback, useState } from 'react'
import { Trans } from 'react-i18next'
import { CautionText, Icons, InformationModal } from 'utoniq-ui'

import {
  DeliveryItemBoxFragment,
  DeliveryItemsConnectionFragment,
  DeliveryManagementQuery,
  DeliveryStatus,
  Maybe,
} from '@/clients/utoniq-core/schema'
import { BaseLayout, Head } from '@/components/layouts'
import { DeliveryManagementBox } from '@/components/organisms'
import { ScrollFetcher, Tabs } from '@/components/organisms/functionals'
import { config } from '@/config'
import { useCurrentUser, useRouter, useTranslation } from '@/core'
import { Cell, CellFrame, Template } from '@/core/architecture'
import { useAnalytics } from '@/lib/analytics'
import { openIntercom, updateIntercom } from '@/lib/intercom'

export * from './fragments'

export type State = {
  initialTab: DeliveryStatus[]
  loading: boolean
  merchItemDeliveryApplying: boolean
  hasNextPage: boolean
  sendingVerification: boolean
  deliveryItems: Maybe<DeliveryItemsConnectionFragment>
  user: Maybe<DeliveryManagementQuery['user']>
}

type CurrentEmailAddressCellProps = {
  loading: State['loading']
  user: State['user']
  onSendVerificationSMS: Action['onSendVerificationSMS']
}

type ListCellProps = {
  loading: boolean
  applying: boolean
  hasNextPage: boolean
  hasRegisterAddress: boolean
  deliveryItems: State['deliveryItems']
  onApply: Action['onApply']
  onFetchMore: () => Promise<void>
  hideSerial?: boolean
  shortAddress: string
}

/**
 * Action type.
 * use key name with 'on' prefix like onLogin.
 */
export type Action = {
  onApply: (delivery: DeliveryItemBoxFragment) => Promise<void>
  onFetchMore: (delivery: DeliveryStatus[]) => Promise<void>
  onChangeStatus: (status: DeliveryStatus[]) => Promise<void>
  onSendVerificationSMS: () => Promise<void>
}

export const DeliveryManagementTemplate: Template<State, Action> = ({
  state,
  onApply,
  onFetchMore,
  onChangeStatus,
  onSendVerificationSMS,
}) => {
  const { user } = useCurrentUser()
  const { t } = useTranslation('deliveryManagement')
  const { routerNames, routerPages } = useRouter()
  const [currentTab, setCurrentTab] = useState<number>(
    +state.initialTab.includes(DeliveryStatus.Arranging)
  )

  const handleChangeTab = useCallback(
    async (tab: number) => {
      setCurrentTab(tab)
      await onChangeStatus(
        tab
          ? [DeliveryStatus.Arranging, DeliveryStatus.Shipped]
          : [DeliveryStatus.Unapplied]
      )
    },
    [onChangeStatus]
  )

  return (
    <BaseLayout
      header={{
        back: true,
        defaultBackPage: routerPages.Management,
        title: t('title'),
      }}
    >
      <Head title={routerNames.DeliveryManagement} />
      <Box sx={{ px: 5, pt: 5 }}>
        <CautionText type="info" sx={{ bgcolor: 'grey.50' }}>
          <Trans
            i18nKey="deliveryInfo"
            t={t}
            components={{
              linkTag: (
                <Typography
                  component="span"
                  variant="subtitle2"
                  color="primary"
                  sx={{ cursor: 'pointer' }}
                  onClick={() => {
                    updateIntercom(user)
                    openIntercom()
                  }}
                />
              ),
            }}
          />
        </CautionText>
      </Box>
      <CurrentEmailAddressCell
        user={state.user}
        loading={state.sendingVerification}
        onSendVerificationSMS={onSendVerificationSMS}
      />
      <Tabs
        // fix bug not show item
        disableAnimateTransitions
        labels={[t('unapplied'), t('applied')]}
        value={currentTab}
        onChange={handleChangeTab}
      >
        <ListCell
          loading={state.loading}
          applying={state.merchItemDeliveryApplying}
          hasNextPage={state.hasNextPage}
          onFetchMore={() => onFetchMore([DeliveryStatus.Unapplied])}
          onApply={onApply}
          hasRegisterAddress={!!state.user?.address?.fullAddress}
          deliveryItems={state.deliveryItems}
          shortAddress={state.user?.address?.shortAddress ?? ''}
        />
        <ListCell
          loading={state.loading}
          applying={state.merchItemDeliveryApplying}
          hasNextPage={state.hasNextPage}
          onFetchMore={() =>
            onFetchMore([DeliveryStatus.Arranging, DeliveryStatus.Shipped])
          }
          onApply={onApply}
          hasRegisterAddress={!!state.user?.address?.fullAddress}
          deliveryItems={state.deliveryItems}
          shortAddress={state.user?.address?.shortAddress ?? ''}
        />
      </Tabs>
    </BaseLayout>
  )
}

export const CurrentEmailAddressCell: Cell<CurrentEmailAddressCellProps> = ({
  user,
  loading,
  onSendVerificationSMS,
}) => {
  const { t } = useTranslation('deliveryManagement')
  const { routerPages } = useRouter()
  const { analytics } = useAnalytics()

  return (
    <CellFrame
      sx={{
        px: 5,
        py: 4,
        mx: 5,
        border: 'solid 1px',
        borderColor: 'grey.200',
        borderRadius: '8px',
        mt: 5,
        mb: 7,
      }}
    >
      <Stack justifyContent="space-between" flexDirection="row">
        <Typography variant="h5" color="text.secondary">
          {t('shippingInfo')}
        </Typography>
        <Button
          variant="text"
          sx={{ mr: -2 }}
          href={routerPages.DeliveryInfoSetting}
          onClick={() => {
            analytics?.track('click_registerShippingInfo', {
              applicationKey: config.applicationKey,
              type: user?.address?.fullAddress ? 'update' : 'register',
            })
          }}
        >
          {t(user?.address?.fullAddress ? 'change' : 'register')}
        </Button>
      </Stack>
      {!user?.address && (
        <Typography variant="body2" color="error.main">
          {t('unregisteredShippingInfo')}
        </Typography>
      )}
      <Stack spacing={1} sx={{ mt: 5 }}>
        <Typography variant="subtitle1" color="text.secondary">
          {t('shippingAddress')}
        </Typography>
        <Typography
          variant="body2"
          color={user?.address?.fullAddress ? 'text.primary' : 'text.hint'}
        >
          {user?.address?.fullAddress ?? t('unregisteredShippingAddress')}
        </Typography>
      </Stack>
      <Stack spacing={1} sx={{ pt: 4 }}>
        <Stack direction="row" spacing={3} alignItems="center">
          <Typography variant="subtitle1" color="text.secondary">
            {t('phoneNumber')}
          </Typography>
          {!user?.isPhoneNumberVerified ? (
            <Stack direction="row" spacing={1} alignItems="center">
              <Icons.Error
                fontSize="small"
                sx={{ color: 'error.main', mt: -1 }}
              />
              <Typography variant="body2" color="error.main">
                {t('unverify')}
              </Typography>
              {user?.phoneNumber && (
                <Button
                  variant="text"
                  color="primary"
                  loading={loading}
                  onClick={onSendVerificationSMS}
                >
                  {t('verify')}
                </Button>
              )}
            </Stack>
          ) : (
            <Stack direction="row" spacing={1} alignItems="center">
              <Icons.Success
                fontSize="small"
                sx={{ color: 'text.hint', mt: -1 }}
              />
              <Typography variant="body2" color="text.hint">
                {t('verified')}
              </Typography>
            </Stack>
          )}
        </Stack>
        <Typography variant="body2">{user?.phoneNumber}</Typography>
        {!user?.phoneNumber && (
          <Typography variant="body2" color="text.hint">
            {t('unregistered')}
          </Typography>
        )}
      </Stack>
    </CellFrame>
  )
}

export const ListCell: Cell<ListCellProps> = ({
  loading,
  applying,
  hasNextPage,
  deliveryItems,
  hasRegisterAddress,
  onApply,
  onFetchMore,
  hideSerial,
  shortAddress,
}) => {
  const { t } = useTranslation('deliveryManagement')
  const { routerPages } = useRouter()
  const [open, setOpen] = useState<boolean>(false)
  const [openRegister, setOpenRegister] = useState<boolean>(false)
  const [delivery, setDelivery] = useState<Maybe<DeliveryItemBoxFragment>>(null)
  const handleClose = useCallback(() => {
    setOpen(false)
    setOpenRegister(false)
    setDelivery(null)
  }, [])

  const handleOpenDialogApply = useCallback(
    (delivery: DeliveryItemBoxFragment) => {
      if (hasRegisterAddress) {
        setOpen(true)
        setDelivery(delivery)
        return
      }
      setOpenRegister(true)
    },
    [hasRegisterAddress]
  )

  const handleApply = useCallback(async () => {
    if (!delivery) {
      return
    }
    await onApply(delivery)
    setOpen(false)
    setDelivery(null)
  }, [delivery, onApply])

  return (
    <CellFrame sx={{ pb: 6, px: 5 }}>
      <ScrollFetcher
        nodes={deliveryItems?.edges?.map(({ node }) => (
          <DeliveryManagementBox
            key={node.id}
            delivery={node}
            onApply={handleOpenDialogApply}
            hideSerial={!!node.item.hideSalesNumber}
          />
        ))}
        loading={loading}
        hasNextPage={hasNextPage}
        onFetchMore={onFetchMore}
        componentProps={{ spacing: 5 }}
        emptyMessage={t('deliveryEmpty')}
      />
      <InformationModal
        onClose={handleClose}
        open={open}
        title={t('applyForShippingTitle')}
        content={
          <Stack spacing={3} sx={{ pb: 7 }}>
            <Stack>
              <Typography variant="subtitle1" textAlign={'left'}>
                {t('applyForShippingDescription1')}
              </Typography>
              <Typography variant="body2" textAlign={'left'}>
                {t('applyForShippingDescription2')}
              </Typography>
            </Stack>
            <Stack>
              <Typography variant="body2" align="left">
                {t('shippingAddress')}
              </Typography>
              <Stack direction="row" alignItems="center">
                <Typography variant="body2" align="left" width="100%">
                  {shortAddress}
                </Typography>
                <Button variant="text" href={routerPages.DeliveryInfoSetting}>
                  {t('changeAddress')}
                </Button>
              </Stack>
            </Stack>
          </Stack>
        }
        cancelText={t('close')}
        buttons={
          <Button
            size="large"
            sx={{ width: 212, whiteSpace: 'nowrap' }}
            onClick={handleApply}
            loading={applying}
            disabled={!shortAddress}
          >
            {t('apply')}
          </Button>
        }
      />
      <InformationModal
        onClose={handleClose}
        open={openRegister}
        title={t('addressNotRegisteredTitle')}
        content={t('addressNotRegisteredDescription')}
        cancelText={t('close')}
        buttons={
          <Button
            size="large"
            sx={{ width: 212 }}
            href={routerPages.DeliveryInfoSetting}
          >
            {t('registerAddress')}
          </Button>
        }
      />
    </CellFrame>
  )
}
